'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Tag, Briefcase, Car, Cog, Truck, BookOpen, CalendarCheck, Building2, UtensilsCrossed, Hotel, Newspaper, Video, Heart, Pill, Package, Zap, Clock, Bell, TrendingUp, MapPin, ArrowRight, Wifi, WifiOff } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';


interface LiveListing {
  id: string;
  title: string;
  module: string;
  price?: string;
  location?: string;
  seller?: string;
  created_at: string;
  href: string;
  isNew?: boolean;
}

interface ModuleConfig {
  label: string;
  icon: React.ElementType;
  color: string;
  href: string;
  emoji: string;
}

const MODULE_CONFIG: Record<string, ModuleConfig> = {
  classifieds: { label: 'Classifieds', icon: Tag, color: '#FFB300', href: '/classifieds', emoji: '🏷️' },
  jobs: { label: 'Jobs', icon: Briefcase, color: '#00BCD4', href: '/jobs', emoji: '💼' },
  'auto-parts': { label: 'Auto Parts', icon: Car, color: '#FF6D00', href: '/auto-parts', emoji: '🔧' },
  machinery: { label: 'Machinery', icon: Cog, color: '#FFB300', href: '/machinery', emoji: '⚙️' },
  delivery: { label: 'Delivery', icon: Truck, color: '#7C4DFF', href: '/delivery', emoji: '📦' },
  education: { label: 'Education', icon: BookOpen, color: '#00BCD4', href: '/education', emoji: '📚' },
  bookings: { label: 'Bookings', icon: CalendarCheck, color: '#00C853', href: '/bookings', emoji: '📅' },
  business: { label: 'Business', icon: Building2, color: '#00BCD4', href: '/business', emoji: '🏢' },
  food: { label: 'Food', icon: UtensilsCrossed, color: '#FF6D00', href: '/food', emoji: '🍽️' },
  hotels: { label: 'Hotels', icon: Hotel, color: '#FF6D00', href: '/hotels', emoji: '🏨' },
  news: { label: 'News', icon: Newspaper, color: '#00BCD4', href: '/news', emoji: '📰' },
  videos: { label: 'Videos', icon: Video, color: '#FF3B5C', href: '/videos', emoji: '🎬' },
  dating: { label: 'Dating', icon: Heart, color: '#FF4081', href: '/dating', emoji: '💕' },
  pharmacy: { label: 'Pharmacy', icon: Pill, color: '#40C4FF', href: '/pharmacy', emoji: '💊' },
  transport: { label: 'Transport', icon: Truck, color: '#00C853', href: '/transport', emoji: '🚌' },
  events: { label: 'Events', icon: CalendarCheck, color: '#E040FB', href: '/events', emoji: '🎉' },
  marketplace: { label: 'Marketplace', icon: Package, color: '#00C853', href: '/marketplace', emoji: '🛒' },
};

// Seed data for initial display (simulates real listings)
const SEED_LISTINGS: LiveListing[] = [
  { id: 's1', title: 'iPhone 15 Pro Max 256GB — Space Black', module: 'classifieds', price: 'KES 145,000', location: 'Nairobi CBD', seller: 'Peter M', created_at: new Date(Date.now() - 2 * 60000).toISOString(), href: '/classifieds' },
  { id: 's2', title: 'Senior Software Engineer at Safaricom', module: 'jobs', price: 'KES 150K–200K', location: 'Nairobi', seller: 'Safaricom PLC', created_at: new Date(Date.now() - 5 * 60000).toISOString(), href: '/jobs' },
  { id: 's3', title: 'Toyota Harrier 2.4 Engine (2AZ-FE)', module: 'auto-parts', price: 'KES 85,000', location: 'Nairobi', seller: 'AutoParts Kenya', created_at: new Date(Date.now() - 8 * 60000).toISOString(), href: '/auto-parts' },
  { id: 's4', title: 'Nairobi Tech Summit 2026 — Tickets Available', module: 'events', price: 'KES 2,500', location: 'KICC, Nairobi', seller: 'TechKenya', created_at: new Date(Date.now() - 12 * 60000).toISOString(), href: '/events' },
  { id: 's5', title: 'Python Programming for Beginners — Moringa School', module: 'education', price: 'KES 45,000', location: 'Nairobi', seller: 'Moringa School', created_at: new Date(Date.now() - 15 * 60000).toISOString(), href: '/education' },
  { id: 's6', title: 'Caterpillar 320D Excavator 2015', module: 'machinery', price: 'KES 4,500,000', location: 'Nairobi', seller: 'Heavy Mach Kenya', created_at: new Date(Date.now() - 20 * 60000).toISOString(), href: '/machinery' },
  { id: 's7', title: 'Nyama Choma Platter (1kg) — Carnivore Nairobi', module: 'food', price: 'KES 2,500', location: 'Nairobi', seller: 'Carnivore', created_at: new Date(Date.now() - 25 * 60000).toISOString(), href: '/food' },
  { id: 's8', title: 'Serena Hotel Nairobi — Rooms Available', module: 'hotels', price: 'KES 18,500/night', location: 'Nairobi', seller: 'Serena Hotels', created_at: new Date(Date.now() - 30 * 60000).toISOString(), href: '/hotels' },
  { id: 's9', title: 'Nairobi–Mombasa Express Bus — Daily Departures', module: 'transport', price: 'KES 1,200', location: 'Nairobi', seller: 'Modern Coast', created_at: new Date(Date.now() - 35 * 60000).toISOString(), href: '/transport' },
  { id: 's10', title: 'Full Hair Braiding & Styling — Amina Beauty Studio', module: 'bookings', price: 'KES 1,500', location: 'Nairobi CBD', seller: 'Amina Beauty', created_at: new Date(Date.now() - 40 * 60000).toISOString(), href: '/bookings' },
  { id: 's11', title: 'Nairobi Hardware & Building Supplies', module: 'business', price: 'Wholesale', location: 'Industrial Area', seller: 'Nairobi Hardware', created_at: new Date(Date.now() - 45 * 60000).toISOString(), href: '/business' },
  { id: 's12', title: 'Paracetamol 500mg (24 tabs) — Fast Delivery', module: 'pharmacy', price: 'KES 85', location: 'Nairobi', seller: 'Nairobi Pharmacy Plus', created_at: new Date(Date.now() - 50 * 60000).toISOString(), href: '/pharmacy' },
];

function timeAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const ALL_MODULES = ['All Modules', ...Object.keys(MODULE_CONFIG)];

export default function LiveListingsPage() {
  const [listings, setListings] = useState<LiveListing[]>(SEED_LISTINGS);
  const [filteredModule, setFilteredModule] = useState('All Modules');
  const [isConnected, setIsConnected] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [ticker, setTicker] = useState(0);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  // Tick every second to update relative timestamps
  useEffect(() => {
    const interval = setInterval(() => setTicker(t => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  // Supabase Realtime subscription
  useEffect(() => {
    const supabase = createClient();
    setIsLoading(false);

    // Subscribe to seller_listings table inserts
    const channel = supabase
      .channel('live-listings-public')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'seller_listings' },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const newListing: LiveListing = {
            id: String(row.id || Date.now()),
            title: String(row.title || 'New Listing'),
            module: String(row.module || 'classifieds'),
            price: row.price ? `KES ${String(row.price)}` : undefined,
            location: row.location ? String(row.location) : undefined,
            seller: row.seller_name ? String(row.seller_name) : undefined,
            created_at: String(row.created_at || new Date().toISOString()),
            href: `/${String(row.module || 'classifieds')}`,
            isNew: true,
          };
          setListings(prev => [newListing, ...prev].slice(0, 50));
          setNewCount(c => c + 1);
          setLastUpdated(new Date());
          // Remove "new" badge after 5s
          setTimeout(() => {
            setListings(prev => prev.map(l => l.id === newListing.id ? { ...l, isNew: false } : l));
          }, 5000);
        }
      )
      .subscribe((status) => {
        setIsConnected(status === 'SUBSCRIBED');
      });

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = filteredModule === 'All Modules'
    ? listings
    : listings.filter(l => l.module === filteredModule);

  const moduleStats = Object.entries(MODULE_CONFIG).map(([key, cfg]) => ({
    key,
    ...cfg,
    count: listings.filter(l => l.module === key).length,
  })).filter(m => m.count > 0);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #001a0a 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #00C853 0%, transparent 50%), radial-gradient(circle at 70% 30%, #00BCD4 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
              style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853', border: '1px solid rgba(0,200,83,0.3)' }}>
              <Zap size={12} />
              <span className="relative flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#00C853' }} />
                Live Listings Feed
              </span>
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              <span style={{ color: '#00C853' }}>Real-Time</span> Listings Across All Modules
            </h1>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Discover freshly listed items from classifieds, jobs, events, food, hotels and more — updated live without refreshing.
            </p>

            {/* Live status bar */}
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-1.5">
                {isConnected ? (
                  <><Wifi size={13} style={{ color: '#00C853' }} /><span className="text-xs font-600" style={{ color: '#00C853' }}>Live</span></>
                ) : (
                  <><WifiOff size={13} style={{ color: '#FFB300' }} /><span className="text-xs font-600" style={{ color: '#FFB300' }}>Connecting…</span></>
                )}
              </div>
              <div className="w-px h-4" style={{ background: 'rgba(255,255,255,0.15)' }} />
              <div className="flex items-center gap-1.5">
                <TrendingUp size={13} style={{ color: '#00BCD4' }} />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{listings.length} listings</span>
              </div>
              <div className="w-px h-4" style={{ background: 'rgba(255,255,255,0.15)' }} />
              <div className="flex items-center gap-1.5">
                <Clock size={13} style={{ color: 'rgba(255,255,255,0.4)' }} />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Updated {timeAgo(lastUpdated.toISOString())}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Module stats row */}
        <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button onClick={() => setFilteredModule('All Modules')}
                className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-600 transition-all"
                style={{
                  background: filteredModule === 'All Modules' ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${filteredModule === 'All Modules' ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.08)'}`,
                  color: filteredModule === 'All Modules' ? '#00C853' : 'rgba(255,255,255,0.55)',
                }}>
                <span>All</span>
                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-700"
                  style={{ background: filteredModule === 'All Modules' ? 'rgba(0,200,83,0.2)' : 'rgba(255,255,255,0.08)', color: filteredModule === 'All Modules' ? '#00C853' : 'rgba(255,255,255,0.4)' }}>
                  {listings.length}
                </span>
              </button>
              {moduleStats.map(m => {
                const Icon = m.icon;
                const active = filteredModule === m.key;
                return (
                  <button key={m.key} onClick={() => setFilteredModule(m.key)}
                    className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-600 transition-all"
                    style={{
                      background: active ? `${m.color}20` : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${active ? m.color + '40' : 'rgba(255,255,255,0.08)'}`,
                      color: active ? m.color : 'rgba(255,255,255,0.55)',
                    }}>
                    <span>{m.emoji}</span>
                    <span>{m.label}</span>
                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-700"
                      style={{ background: active ? `${m.color}25` : 'rgba(255,255,255,0.08)', color: active ? m.color : 'rgba(255,255,255,0.4)' }}>
                      {m.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* New items notification */}
        {newCount > 0 && (
          <div className="sticky top-0 z-30 px-4 py-2"
            style={{ background: 'rgba(0,200,83,0.1)', borderBottom: '1px solid rgba(0,200,83,0.2)' }}>
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={14} style={{ color: '#00C853' }} />
                <span className="text-sm font-600" style={{ color: '#00C853' }}>
                  {newCount} new listing{newCount > 1 ? 's' : ''} just added!
                </span>
              </div>
              <button onClick={() => setNewCount(0)}
                className="text-xs px-3 py-1 rounded-lg font-600"
                style={{ background: 'rgba(0,200,83,0.2)', color: '#00C853' }}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Listings feed */}
        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                <span style={{ color: '#00C853', fontWeight: 700 }}>{filtered.length}</span> listings
                {filteredModule !== 'All Modules' && <span> in {MODULE_CONFIG[filteredModule]?.label}</span>}
              </span>
            </div>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#00C853', color: '#000' }}>
              Post Listing <ArrowRight size={12} />
            </Link>
          </div>

          {isLoading ? (
            <div className="flex flex-col gap-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-20 rounded-2xl animate-pulse"
                  style={{ background: 'rgba(255,255,255,0.04)' }} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map(listing => {
                const cfg = MODULE_CONFIG[listing.module];
                if (!cfg) return null;
                const Icon = cfg.icon;
                return (
                  <Link key={listing.id} href={listing.href}
                    className="flex items-center gap-3 p-3 rounded-2xl transition-all hover:scale-[1.01] relative overflow-hidden"
                    style={{
                      background: listing.isNew ? `${cfg.color}08` : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${listing.isNew ? cfg.color + '30' : 'rgba(255,255,255,0.07)'}`,
                    }}>
                    {/* New badge pulse */}
                    {listing.isNew && (
                      <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-md"
                        style={{ background: `${cfg.color}20`, border: `1px solid ${cfg.color}40` }}>
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: cfg.color }} />
                        <span className="text-[9px] font-700" style={{ color: cfg.color }}>NEW</span>
                      </div>
                    )}

                    {/* Module icon */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}25` }}>
                      <Icon size={18} style={{ color: cfg.color }} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-700 text-white line-clamp-1 pr-12">{listing.title}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md font-600"
                          style={{ background: `${cfg.color}15`, color: cfg.color }}>
                          {cfg.emoji} {cfg.label}
                        </span>
                        {listing.price && (
                          <span className="text-xs font-700" style={{ color: cfg.color }}>{listing.price}</span>
                        )}
                        {listing.location && (
                          <div className="flex items-center gap-1">
                            <MapPin size={9} style={{ color: 'rgba(255,255,255,0.35)' }} />
                            <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{listing.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 ml-auto">
                          <Clock size={9} style={{ color: 'rgba(255,255,255,0.3)' }} />
                          <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{timeAgo(listing.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Module quick links */}
          <div className="mt-8">
            <h2 className="text-sm font-700 text-white mb-4">Browse All Modules</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {Object.entries(MODULE_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <Link key={key} href={cfg.href}
                    className="flex items-center gap-3 p-3 rounded-2xl transition-all hover:scale-[1.02]"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}25` }}>
                      <Icon size={16} style={{ color: cfg.color }} />
                    </div>
                    <div>
                      <p className="text-xs font-700 text-white">{cfg.label}</p>
                      <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        {listings.filter(l => l.module === key).length} listed
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,200,83,0.1), rgba(0,188,212,0.1))', border: '1px solid rgba(0,200,83,0.2)' }}>
            <div className="flex items-center justify-center gap-2 mb-3">
              <Zap size={18} style={{ color: '#00C853' }} />
              <h3 className="text-lg font-800 text-white">Start Listing Today</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Your listing appears here in real-time the moment you publish it. Reach thousands of buyers instantly.
            </p>
            <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#00C853', color: '#000' }}>
              <Zap size={16} /> Post Your First Listing
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
